import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

// Chat endast för superusers (DB) och testkonto. ALLOWED_* är fallback om superuser inte är satt.
const ALLOWED_USER_IDS = new Set<string>([
  "8053e47e-b01d-4761-8c93-bf42e21e1403", // Sanna Dahlin
]);

const ALLOWED_EMAILS = new Set<string>([
  "testanvandare1234@gmail.com", // QA test user
]);

const normalizeEmail = (email?: string | null) => (email || "").trim().toLowerCase();

export const useFindigoChatAccess = () => {
  const location = useLocation();
  const [allowed, setAllowed] = useState(false);
  const [loading, setLoading] = useState(true);

  // Dev-only: ?chat=1 on dashboard (not on auth/onboarding) to show chat without auth for manual testing.
  const isOnboardingRoute = location.pathname === "/auth" || location.pathname === "/onboarding";
  const devChatBypass =
    !isOnboardingRoute &&
    typeof import.meta !== "undefined" &&
    !!import.meta.env?.DEV &&
    (location.search?.includes("chat=1") || window.location?.search?.includes("chat=1"));

  useEffect(() => {
    if (devChatBypass) {
      setAllowed(true);
      setLoading(false);
      return;
    }

    const evaluateAccess = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const userId = session?.user?.id || null;
        const sessionEmail = normalizeEmail(session?.user?.email);
        if (!userId) {
          setAllowed(false);
          return;
        }

        const isAllowedById = ALLOWED_USER_IDS.has(userId);
        const isAllowedBySessionEmail = sessionEmail && ALLOWED_EMAILS.has(sessionEmail);

        let profileEmail = "";
        try {
          const { data: profile, error } = await supabase
            .from("profiles")
            .select("email")
            .eq("user_id", userId)
            .maybeSingle();
          if (!error && profile) {
            profileEmail = normalizeEmail(profile.email);
          }
        } catch {
          // ignore - fail safe to disabled
        }

        const isAllowedByProfileEmail =
          profileEmail.length > 0 && ALLOWED_EMAILS.has(profileEmail);

        // Superusers (from DB) always get chat access. Otherwise only explicit allow list (me + test account).
        let isSuperuser = false;
        try {
          const { data, error } = await supabase.rpc("check_superuser", {
            p_user_id: userId,
          });
          if (!error) isSuperuser = Boolean(data);
        } catch {
          // ignore
        }

        const isAllowed =
          isSuperuser ||
          isAllowedById ||
          isAllowedBySessionEmail ||
          isAllowedByProfileEmail;

        setAllowed(Boolean(isAllowed));
      } finally {
        setLoading(false);
      }
    };

    evaluateAccess();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(() => {
      evaluateAccess();
    });

    return () => subscription.unsubscribe();
  }, [devChatBypass]);

  return { allowed, loading };
};
